/**
 * German master leases: what we actually paid per flat, per month, and how
 * much of that rent was covered by client bookings.
 *
 * WHY (Fan, 2026-09-03): "The German master leases are also COGS! They are
 * supplier accommodation." and "please consider using the master leases and
 * noting that in BR OS as well. So they know we paid for example".
 *
 * Each lease euro is counted once: the booking supplier cost on a flat already
 * carries the rent, so only the uncovered part (the void) is a cost on top.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { brosTaxReservesAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

function num(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function monthLabel(month) {
  if (!month) return '—';
  const [year, mon] = String(month).split('-');
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[Number(mon)] ?? mon} ${year}`;
}

function LeaseRow({ lease }) {
  const [open, setOpen] = useState(false);
  const currency = lease.currency || 'EUR';
  return (
    <>
      <tr className="border-t cursor-pointer hover:bg-muted/40" onClick={() => setOpen((v) => !v)}>
        <td className="py-1.5 pr-2">
          <span className="inline-flex items-center gap-1">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {lease.property || lease.bros_property_id}
          </span>
        </td>
        <td className="text-right py-1.5">{formatCurrency(num(lease.rent_charged), currency)}</td>
        <td className="text-right py-1.5">{formatCurrency(num(lease.covered_by_bookings), currency)}</td>
        <td className="text-right py-1.5 font-medium">{formatCurrency(num(lease.void), currency)}</td>
      </tr>
      {open &&
        (lease.months || []).map((m) => (
          <tr key={`${lease.bros_property_id}-${m.month}`} className="text-[11px] text-muted-foreground">
            <td className="py-1 pl-6">
              {monthLabel(m.month)} {m.occupied ? '' : '· empty'}
            </td>
            <td className="text-right py-1">{formatCurrency(num(m.rent_charged), currency)}</td>
            <td className="text-right py-1">{formatCurrency(num(m.covered_by_bookings), currency)}</td>
            <td className="text-right py-1">{formatCurrency(num(m.void), currency)}</td>
          </tr>
        ))}
    </>
  );
}

export function BROSMasterLeases({ year = new Date().getFullYear() }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await brosTaxReservesAPI.getMasterLeases({ year });
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid="master-leases-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading master leases…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-destructive py-4" data-testid="master-leases-error">
        {error}
      </div>
    );
  }
  const leases = data?.leases || [];
  if (!leases.length) return null;
  const totals = data?.totals || {};
  const currency = leases[0]?.currency || 'EUR';

  return (
    <div data-testid="bros-master-leases">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">
        German master leases ({data?.year ?? year}) — supplier accommodation we paid for
      </h2>
      <p className="text-[11px] text-muted-foreground mb-2">
        {data?.note ||
          'Rent on these flats is already inside the supplier cost of the bookings placed in them. Only the uncovered rent (void) is an extra cost.'}
      </p>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left font-medium py-1">Property</th>
            <th className="text-right font-medium py-1">Rent charged</th>
            <th className="text-right font-medium py-1">Covered by bookings</th>
            <th className="text-right font-medium py-1">Void (extra cost)</th>
          </tr>
        </thead>
        <tbody>
          {leases.map((lease) => (
            <LeaseRow key={lease.bros_property_id} lease={lease} />
          ))}
          <tr className="border-t font-semibold">
            <td className="py-1.5">Total</td>
            <td className="text-right py-1.5">{formatCurrency(num(totals.rent_charged), currency)}</td>
            <td className="text-right py-1.5">{formatCurrency(num(totals.covered_by_bookings), currency)}</td>
            <td className="text-right py-1.5">{formatCurrency(num(totals.void), currency)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default BROSMasterLeases;
